import {
	createActionsHook,
	createContainer,
	createDefaultEocWrapper,
	createStore,
	createHook,
} from '@atlassian/eoc-rss-helpers';
import { useFlagService } from '@atlassian/jira-flags';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { emptyResource } from '../../../common/constants';
import type { StoreBaseContainerProps } from '../../../common/types';
import { actions } from './actions';
import type { IntegrationsState } from './types';

export const initialState: IntegrationsState = {
	counts: emptyResource,
	syncedIntegrations: emptyResource,
	discontinuedIntegrations: emptyResource,
	deprecatedIntegrations: emptyResource,
	incomingCallIntegrations: emptyResource,
};

const Store = createStore({ initialState, actions, name: 'integrations' });

const IntegrationsContainer = createContainer<
	IntegrationsState,
	typeof actions,
	{
		children?: React.ReactNode;
		isGlobal?: boolean;
	}
>(Store, {
	Wrapper: createDefaultEocWrapper({
		useSyncContainerProps: (): StoreBaseContainerProps => {
			const { cloudId } = useTenantContext();
			const { showFlag } = useFlagService();
			return {
				cloudId,
				showFlag,
			};
		},
	}),
});

const useIntegrationsStore = createHook(Store);
const useIntegrationsActions = createActionsHook(Store);

const useIntegrationCounts = createHook(Store, {
	selector: (state) => ({
		counts: state.counts,
		hasIntegrations: (state.counts.data?.totalCount || 0) > 0,
	}),
});

const useSyncedIntegrations = createHook(Store, {
	selector: (state) => ({
		syncedIntegrations: state.syncedIntegrations,
		hasSyncedIntegration: (state.syncedIntegrations.data?.count || 0) > 0,
	}),
});

const useDiscontinuedIntegrations = createHook(Store, {
	selector: (state) => ({
		discontinuedIntegrations: state.discontinuedIntegrations,
		hasDiscontinuedIntegration: (state.discontinuedIntegrations.data?.count || 0) > 0,
	}),
});

const useDeprecatedIntegrations = createHook(Store, {
	selector: (state) => ({
		deprecatedIntegrations: state.deprecatedIntegrations,
		hasDeprecatedIntegration: (state.deprecatedIntegrations.data?.count || 0) > 0,
	}),
});

const useIncomingCallIntegrations = createHook(Store, {
	selector: (state) => ({
		incomingCallIntegrations: state.incomingCallIntegrations,
		hasIncomingCallIntegration: (state.incomingCallIntegrations.data?.count || 0) > 0,
	}),
});

export {
	IntegrationsContainer,
	useIntegrationsStore,
	useIntegrationsActions,
	useIntegrationCounts,
	useSyncedIntegrations,
	useDiscontinuedIntegrations,
	useDeprecatedIntegrations,
	useIncomingCallIntegrations,
};
